import React, {useRef, useState} from "react";
import {useParams} from "react-router";
import {UserRole} from "../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { Button } from "@/components/ui/button";
import PageHeader from "../components/common/Page/PageHeader";
import {ActiveJobsExplorer} from "../models/Jobs/ActiveJobsExplorer";
import {ProviderFilesTable, ProviderFilesTableRef } from "../components/ProviderFiles/ProviderFilesTable";
import { ProviderFile } from "../models/ProviderFile";
import { ExportProviderFilesToCsvStatusTable } from "../components/ProviderFiles/ExportProviderFilesToCsvStatusTable";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface ProviderFilesPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

export const ProviderFilesPage: React.FC<ProviderFilesPageProps> = ({
  userRole,
  isAssignedUser
}: ProviderFilesPageProps) => {
  const requiresReason = useRequiresReason();
  const { organizationId } = useParams();
  const [isConfirmExportModalOpen, setIsConfirmExportModalOpen] = React.useState<boolean>(false);
  const [newProviderFilesForDeletion, setNewProviderFilesForDeletion] = useState<ProviderFile[]>([]);
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(0);
  const providerFilesTableRef = useRef<ProviderFilesTableRef>(null);

  const startCurrentViewExportToCsv = (reason?: string) =>
    providerFilesTableRef.current?.exportCurrentViewToCsv(reason);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <PageHeader
        title="Provider Files"
        action={userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage exports
          </Button>
        )}
      />
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <ProviderFilesTable
            ref={providerFilesTableRef}
            organizationId={organizationId ?? ""}
            activeJobsExplorer={ActiveJobsExplorer.Export}
            newProviderFilesForDeletion={newProviderFilesForDeletion}
            setNewProvidersFilesForDeletion={setNewProviderFilesForDeletion}
            disabled={userRole === UserRole.Viewer}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage exports</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 gap-4 p-4 overflow-auto">
              {requiresReason ? (
                <ActionConfirmationModal
                  actionConfig={ACTION_CONFIGS.EXPORT_PROVIDER_FILES_VIEW}
                  onConfirm={async (reason) => startCurrentViewExportToCsv(reason)}
                >
                  <Button>Export current files view</Button>
                </ActionConfirmationModal>
              ) : (
                <Button onClick={() => setIsConfirmExportModalOpen(true)}>
                  Export current files view
                </Button>
              )}
              <ExportProviderFilesToCsvStatusTable
                userRole={userRole}
                isAssignedUser={isAssignedUser}
                organizationId={organizationId ?? ""}
              />
            </div>
          </div>
        )}
      </div>
      {isConfirmExportModalOpen && (
        <ConfirmationModal
          open={isConfirmExportModalOpen}
          onClose={() => setIsConfirmExportModalOpen(false)}
          onConfirm={async () => {
            startCurrentViewExportToCsv();
            setIsConfirmExportModalOpen(false);
          }}
          message="Are you sure you want to export provider files from the current view?"
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};
